import {
  SerializedError,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import type { FlowTreeNode, LinearFlowNode } from '@vk-hr-tek/ui/FlowTree';

import {
  EventListItem,
  CancelReasonItem as CancelReason,
  CreateSimpleEventTypeOptionsResponse as EventTypeOptions,
  CreateSimpleEventTypeCompany as EventTypeCompany,
  EventCompetencyOption,
  AbsenceListGroup,
  TimeRange,
  WorkScheduleCalendar,
} from '@app/gen/events';
import { RootState } from '@app/store';
import { AbsenceIntersectionMonth } from '@app/gen/absences';

import { EventWithValidators } from '../types';
import { SHOW_URGENT_EVENTS_LOCAL_STORAGE_KEY } from '../constants';

export interface EventsState {
  status: 'idle' | 'loading' | 'complete' | 'failed';
  currentIds: string[];
  detail: {
    startEventId: string | null;
    currentId: string | null;
    status: 'idle' | 'loading' | 'loading-text' | 'complete' | 'failed';
    actionTypeForLoadingText: {
      eventId: string | null;
      nodeId: string | null;
      actionType: string | null;
      retryText?: string;
    };
    data: EventWithValidators | null;
    error: AppError | null;
    absences: AbsenceListGroup[];
    workSchedule: WorkScheduleCalendar | null;
    print: {
      status: 'idle' | 'loading' | 'complete' | 'failed';
    };
    isCancelDialogOpen?: boolean;
  };
  certificates: {
    items: {
      id: string;
      name: string;
      user: string;
      company: string;
      activeFrom: string;
      activeTo: string;
    }[];
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: null | string;
  };
  total: number;
  totalDocuments: number | null;
  totalMy?: number;
  totalMyTeam?: number;
  totalFromMe?: number;
  parentEventTags: string[];
  finished: number;
  error: AppError | null;
  filters: {
    data: Filter[] | null;
    id: string;
  };
  filtersWithCompanyId: Record<string, Filter[]>;
  cancelReasons: CancelReason[] | null;
  canCreateEvent: boolean;
  canCreateEventType: boolean;
  eventTypes: {
    companies: (EventTypeCompany & EventTypeOptions)[];
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
  };
  pepSign: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  pepSignBatch: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  ukepSign: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  ukepSignBatch: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  unepSign: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  unepSignBatch: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
  };
  companyBatchSign: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    successCounter: { success_count: number; error_count: number } | null;
    error: AppError | null;
  };
  batchDownload: {
    downloads: {
      id: string;
      status: 'waiting' | 'complete' | 'failed';
      counter: number;
    }[];
  };
  flow: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    linearFlowStatus: 'idle' | 'loading' | 'complete' | 'failed';
    tree: FlowTreeNode | null;
    linearTree: LinearFlowNode[] | null;
    startNodeId?: string;
    error: AppError | null;
  };
  printFlow: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
  };
  resendedNodes: {
    eventId: string;
    nodeId: string;
    timestamp: number;
  }[];
  codeRequesting: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    error: AppError | null;
  };

  competencyOptions: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: EventCompetencyOption[];
    error: AppError | null;
  };

  activePeriods: TimeRange[];
  isActiveChildButton: boolean;
  showUrgentEvents: boolean;
  absences: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    additionalStatus:
      | 'idle'
      | 'loading'
      | 'loadingAfterFail'
      | 'complete'
      | 'failed';
    items: AbsenceListGroup[];
    intersections: AbsenceIntersectionMonth[];
    error: AppError | null;
    total: number;
    totalDocuments: number | null;
    totalMy?: number;
    totalMyTeam?: number;
    finished: number;
    filters: Record<string, Filter[]>;
  };
}

export interface EventsWithRootState extends RootState {
  events: EventsState & EntityState<EventListItem, string>;
}

const loadShowUrgentEventsFromLocalStorage = () => {
  try {
    const showUrgentEvents = localStorage.getItem(
      SHOW_URGENT_EVENTS_LOCAL_STORAGE_KEY,
    );

    return typeof showUrgentEvents === 'string'
      ? showUrgentEvents === 'true'
      : true;
  } catch {
    return true;
  }
};

export const initialState: EventsState = {
  status: 'idle',
  currentIds: [],
  detail: {
    startEventId: null,
    currentId: null,
    status: 'idle',
    data: null,
    actionTypeForLoadingText: {
      eventId: null,
      nodeId: null,
      actionType: null,
      retryText: undefined,
    },
    isCancelDialogOpen: undefined,
    error: null,
    absences: [],
    workSchedule: null,
    print: {
      status: 'idle',
    },
  },
  total: 0,
  totalDocuments: null,
  totalMy: undefined,
  totalMyTeam: undefined,
  parentEventTags: [],
  finished: 0,
  error: null,
  filters: {
    data: null,
    id: '',
  },
  filtersWithCompanyId: {},
  certificates: {
    items: [],
    status: 'idle',
    error: null,
  },
  cancelReasons: null,
  canCreateEvent: false,
  canCreateEventType: false,
  eventTypes: {
    companies: [],
    status: 'idle',
    error: null,
  },
  pepSign: {
    status: 'idle',
  },
  pepSignBatch: {
    status: 'idle',
  },
  ukepSign: {
    status: 'idle',
  },
  ukepSignBatch: {
    status: 'idle',
  },
  unepSign: {
    status: 'idle',
  },
  unepSignBatch: {
    status: 'idle',
  },
  companyBatchSign: {
    status: 'idle',
    error: null,
    successCounter: null,
  },
  batchDownload: {
    downloads: [],
  },
  flow: {
    status: 'idle',
    linearFlowStatus: 'idle',
    error: null,
    tree: null,
    linearTree: null,
  },
  printFlow: { status: 'idle', error: null },
  resendedNodes: [],
  codeRequesting: {
    status: 'idle',
    error: null,
  },
  competencyOptions: {
    status: 'idle',
    items: [],
    error: null,
  },
  activePeriods: [],
  isActiveChildButton: true,
  showUrgentEvents: loadShowUrgentEventsFromLocalStorage(),
  absences: {
    additionalStatus: 'idle',
    status: 'idle',
    items: [],
    intersections: [],
    error: null,
    total: 0,
    totalDocuments: null,
    finished: 0,
    filters: {},
  },
};

export const startLoading = (state: EventsState) => {
  state.status = 'loading';
  state.error = null;
  state.total = 0;
  state.finished = 0;
  state.currentIds = [];
  state.detail.data = null;
};

export const completeLoading = (state: EventsState) => {
  state.status = 'complete';
  state.error = null;
};

export const setError = (
  state: EventsState,
  { payload, error }: { payload?: AppError; error: SerializedError },
) => {
  state.status = 'failed';
  state.error =
    payload ||
    ({
      info: (error && error.message) || 'Unknown error',
      status: 500,
      source: 'client',
      title: 'Internal client error',
    } as AppError);
};

export const eventsAdapter = createEntityAdapter<EventListItem>();

export const setCodeRequestingLoading = (state: EventsState) => {
  state.codeRequesting.status = 'loading';
  state.codeRequesting.error = null;
};

export const setCodeRequestingSuccess = (state: EventsState) => {
  state.codeRequesting.status = 'complete';
  state.codeRequesting.error = null;
};

export const setCodeRequestingError = (
  state: EventsState,
  { payload, error }: { payload?: AppError; error: SerializedError },
) => {
  state.codeRequesting.status = 'failed';
  state.codeRequesting.error =
    payload ||
    ({
      info: (error && error.message) || 'Unknown error',
      status: 500,
      source: 'client',
      title: 'Internal client error',
    } as AppError);
};
